// First Plate Onboarding Wizard.
// Triggered on the first login for any user. Three surgical steps that
// produce a real recipe with a Live Margin. High-end V2 True Glass
// modal, gold rim, chromatic prism hairline. Zero em dashes. Clinical
// specialist voice.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChefHat, X, ArrowRight, ArrowLeft, Loader2, Sparkles, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function GlassPanel({ children, className = "", radius = 26 }) {
  return (
    <div
      className={`relative ${className}`}
      style={{
        borderRadius: radius,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.015) 100%)",
        backdropFilter: "blur(44px) saturate(200%)",
        WebkitBackdropFilter: "blur(44px) saturate(200%)",
        border: "1px solid rgba(232,207,154,0.42)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.32)",
          "inset 0 -1px 0 rgba(255,255,255,0.04)",
          "0 60px 140px -30px rgba(0,0,0,0.92)",
          "0 30px 60px -14px rgba(0,0,0,0.7)",
          "0 0 90px -20px rgba(232,207,154,0.35)",
        ].join(","),
      }}
    >
      <div
        aria-hidden
        className="absolute top-0 left-6 right-6 h-px rounded-full pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(232,207,154,0.9), rgba(255,255,255,0.6), rgba(90,178,201,0.85), transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: radius,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, transparent 32%, transparent 70%, rgba(255,255,255,0.05) 100%)",
          mixBlendMode: "screen",
          opacity: 0.65,
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="first-plate-step-dots">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className="h-1 flex-1 rounded-full transition-colors"
          style={{
            background: n <= step ? "linear-gradient(90deg, rgba(232,207,154,0.9), rgba(200,169,106,0.5))" : "rgba(255,255,255,0.08)",
          }}
        />
      ))}
    </div>
  );
}

export default function FirstPlateWizard() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0); // 0 = intro, 1..3 = steps, 4 = result
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    ingredient_name: "",
    purchase_price: "",
    purchase_qty: 1,
    purchase_unit: "kg",
    yield_percent: "",
    portion_size: 250,
    portion_unit: "g",
    sell_price: "",
  });
  const [yieldRationale, setYieldRationale] = useState("");

  // Trigger check on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        if (data?.due) setVisible(true);
      } catch {
        /* silent: unauth or offline just skips */
      }
    })();
  }, []);

  const set = (k, coerce = null) => (e) => {
    const raw = e.target.value;
    const v = coerce === "number" ? raw : raw;
    setForm({ ...form, [k]: v });
  };

  async function suggestYield() {
    if (!form.ingredient_name.trim()) return;
    setSuggesting(true);
    try {
      const { data } = await api.post("/onboarding/yield-suggestion", {
        ingredient: form.ingredient_name.trim(),
      });
      setForm((f) => ({ ...f, yield_percent: String(data.yield_percent) }));
      setYieldRationale(data.rationale || "");
      toast.success(`Chef suggests ${data.yield_percent}% yield.`);
    } catch (err) {
      toast.error("Could not fetch a suggestion right now.");
    } finally {
      setSuggesting(false);
    }
  }

  function next() {
    // Validate current step before moving.
    if (step === 1) {
      const price = parseFloat(form.purchase_price);
      if (!form.ingredient_name.trim() || !(price > 0)) {
        toast.error("Enter the ingredient name and its purchase price.");
        return;
      }
    }
    if (step === 2) {
      const y = parseFloat(form.yield_percent);
      if (!(y >= 30 && y <= 100)) {
        toast.error("Yield must be between 30 and 100 percent.");
        return;
      }
    }
    if (step === 3) {
      const sp = parseFloat(form.sell_price);
      if (!(sp > 0)) {
        toast.error("Enter the sell price of one plate.");
        return;
      }
      return submit();
    }
    setStep(step + 1);
  }

  async function submit() {
    setSaving(true);
    try {
      const payload = {
        ingredient_name: form.ingredient_name.trim(),
        purchase_price: parseFloat(form.purchase_price),
        purchase_qty: parseFloat(form.purchase_qty) || 1,
        purchase_unit: form.purchase_unit,
        yield_percent: parseFloat(form.yield_percent),
        portion_size: parseFloat(form.portion_size) || 250,
        portion_unit: form.portion_unit,
        sell_price: parseFloat(form.sell_price),
      };
      const { data } = await api.post("/onboarding/first-plate", payload);
      setResult(data);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function skip() {
    try {
      await api.post("/onboarding/skip");
    } catch {
      /* silent */
    }
    setVisible(false);
  }

  function done() {
    setVisible(false);
    if (result?.recipe_id) navigate(`/recipes/${result.recipe_id}`);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      style={{ background: "rgba(4,3,2,0.72)", backdropFilter: "blur(6px)" }}
      data-testid="first-plate-wizard"
    >
      <GlassPanel className="w-full max-w-[520px] p-7 md:p-9">
        <button
          onClick={skip}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="first-plate-close"
          title="Skip for now"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>

        {/* INTRO */}
        {step === 0 && (
          <div>
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] mb-4"
              style={{ background: "rgba(232,207,154,0.10)", border: "1px solid rgba(232,207,154,0.35)", color: "#e8cf9a" }}
            >
              <ChefHat className="w-3 h-3" /> First Plate
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-[1.1] mb-3" data-testid="first-plate-title">
              Ready to see your real margins?
            </h2>
            <p className="text-sm md:text-base text-white/75 leading-relaxed mb-8">
              Let's cost your first signature dish. Three surgical steps: your main ingredient, its yield, and your sell price. The system does the math. You see the Live Margin in under a minute.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-sm font-semibold"
                style={{
                  background: "linear-gradient(180deg, rgba(232,207,154,0.42), rgba(200,169,106,0.14))",
                  border: "1px solid rgba(232,207,154,0.65)",
                  color: "#0a0806",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.32), 0 10px 28px -8px rgba(232,207,154,0.45)",
                }}
                data-testid="first-plate-start"
              >
                Start <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={skip}
                className="text-xs text-white/50 hover:text-white/70 px-3 py-2"
                data-testid="first-plate-skip"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div>
            <StepDots step={1} />
            <div className="label-eyebrow gold mb-2">Step 1 of 3</div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3">Your main ingredient</h3>
            <p className="text-sm text-white/65 mb-6">Name it and give us the price you paid the last supplier.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-white/60 mb-1.5">Ingredient name</label>
                <input
                  value={form.ingredient_name}
                  onChange={set("ingredient_name")}
                  placeholder="Lamb Shoulder"
                  className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95"
                  data-testid="first-plate-ingredient-name"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-xs text-white/60 mb-1.5">Quantity</label>
                  <input
                    type="number"
                    value={form.purchase_qty}
                    onChange={set("purchase_qty")}
                    className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95"
                    data-testid="first-plate-purchase-qty"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs text-white/60 mb-1.5">Unit</label>
                  <select
                    value={form.purchase_unit}
                    onChange={set("purchase_unit")}
                    className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95 appearance-none"
                    data-testid="first-plate-purchase-unit"
                  >
                    <option value="kg" className="bg-black">kg</option>
                    <option value="g" className="bg-black">g</option>
                    <option value="lb" className="bg-black">lb</option>
                    <option value="oz" className="bg-black">oz</option>
                    <option value="each" className="bg-black">each</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <label className="block text-xs text-white/60 mb-1.5">Price paid</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.purchase_price}
                    onChange={set("purchase_price")}
                    placeholder="24.00"
                    className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95"
                    data-testid="first-plate-purchase-price"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1.5">Portion per plate</label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    value={form.portion_size}
                    onChange={set("portion_size")}
                    className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95"
                    data-testid="first-plate-portion-size"
                  />
                  <select
                    value={form.portion_unit}
                    onChange={set("portion_unit")}
                    className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95 appearance-none"
                    data-testid="first-plate-portion-unit"
                  >
                    <option value="g" className="bg-black">g</option>
                    <option value="kg" className="bg-black">kg</option>
                    <option value="oz" className="bg-black">oz</option>
                    <option value="lb" className="bg-black">lb</option>
                  </select>
                </div>
              </div>
            </div>

            <NavRow step={step} onBack={() => setStep(0)} onNext={next} nextLabel="Next" saving={saving} />
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div>
            <StepDots step={2} />
            <div className="label-eyebrow gold mb-2">Step 2 of 3</div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3">Set your yield</h3>
            <p className="text-sm text-white/65 mb-6">
              Yield is the percentage of what you bought that ends up on the plate. Bone, fat trim, cook loss. Ask the Chef for the industry standard on your ingredient.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-white/60 mb-1.5">Yield percentage</label>
                <div className="grid grid-cols-[1fr_auto] gap-3">
                  <input
                    type="number"
                    min="30"
                    max="100"
                    value={form.yield_percent}
                    onChange={set("yield_percent")}
                    placeholder="65"
                    className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95"
                    data-testid="first-plate-yield"
                  />
                  <button
                    onClick={suggestYield}
                    disabled={suggesting || !form.ingredient_name.trim()}
                    className="inline-flex items-center justify-center gap-1.5 px-4 rounded-lg text-xs font-medium disabled:opacity-40"
                    style={{
                      background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.10))",
                      border: "1px solid rgba(232,207,154,0.55)",
                      color: "#faf3df",
                    }}
                    data-testid="first-plate-suggest-yield"
                  >
                    {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Ask the Chef
                  </button>
                </div>
                {yieldRationale && (
                  <div className="mt-2 text-[11px] text-white/55 leading-relaxed" data-testid="first-plate-yield-rationale">
                    {yieldRationale}
                  </div>
                )}
              </div>
            </div>

            <NavRow step={step} onBack={() => setStep(1)} onNext={next} nextLabel="Next" saving={saving} />
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div>
            <StepDots step={3} />
            <div className="label-eyebrow gold mb-2">Step 3 of 3</div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3">Your sell price</h3>
            <p className="text-sm text-white/65 mb-6">What does one plate sell for on your menu? The system will calculate the Live Margin from what you have entered.</p>

            <div>
              <label className="block text-xs text-white/60 mb-1.5">Sell price per plate</label>
              <input
                type="number"
                step="0.01"
                value={form.sell_price}
                onChange={set("sell_price")}
                placeholder="42.00"
                className="glass-input w-full h-11 px-3 rounded-lg text-sm text-white/95"
                data-testid="first-plate-sell-price"
              />
            </div>

            <NavRow step={step} onBack={() => setStep(2)} onNext={next} nextLabel="See my Live Margin" saving={saving} />
          </div>
        )}

        {/* RESULT */}
        {step === 4 && result && (
          <div>
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] mb-4"
              style={{ background: "rgba(141,211,164,0.10)", border: "1px solid rgba(141,211,164,0.35)", color: "#a3e5b8" }}
            >
              <Check className="w-3 h-3" /> Live Margin
            </div>
            <h3 className="font-display text-3xl md:text-4xl leading-tight mb-2" data-testid="first-plate-result-title">
              {result.recipe_name}
            </h3>
            <p className="text-sm text-white/60 mb-6">Saved to your Recipes list. Every future dish uses the same math.</p>

            <div className="grid grid-cols-3 gap-3 mb-8">
              <ResultStat label="Plate Cost" value={`$${result.plate_cost.toFixed(2)}`} testId="first-plate-cost" />
              <ResultStat label="Live Margin" value={`$${result.margin.toFixed(2)}`} highlight testId="first-plate-margin" />
              <ResultStat label="Food Cost" value={`${result.food_cost_percent.toFixed(1)}%`} testId="first-plate-fc-pct" />
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={done}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-sm font-semibold"
                style={{
                  background: "linear-gradient(180deg, rgba(232,207,154,0.42), rgba(200,169,106,0.14))",
                  border: "1px solid rgba(232,207,154,0.65)",
                  color: "#0a0806",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.32), 0 10px 28px -8px rgba(232,207,154,0.45)",
                }}
                data-testid="first-plate-open-recipe"
              >
                Open the recipe <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setVisible(false)}
                className="text-xs text-white/50 hover:text-white/70 px-3 py-2"
                data-testid="first-plate-close-result"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function ResultStat({ label, value, highlight = false, testId }) {
  return (
    <div
      className="rounded-xl px-3 py-3 text-center"
      style={{
        background: highlight ? "rgba(141,211,164,0.10)" : "rgba(255,255,255,0.03)",
        border: highlight ? "1px solid rgba(141,211,164,0.35)" : "1px solid rgba(232,207,154,0.20)",
      }}
      data-testid={testId}
    >
      <div className="font-display text-2xl tabular leading-none" style={{ color: highlight ? "#a3e5b8" : "#f5efe0" }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-2">{label}</div>
    </div>
  );
}

function NavRow({ step, onBack, onNext, nextLabel, saving }) {
  return (
    <div className="flex items-center gap-3 mt-8">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white/85 px-3 py-2"
        data-testid={`first-plate-back-${step}`}
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <button
        onClick={onNext}
        disabled={saving}
        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-sm font-semibold disabled:opacity-60"
        style={{
          background: "linear-gradient(180deg, rgba(232,207,154,0.42), rgba(200,169,106,0.14))",
          border: "1px solid rgba(232,207,154,0.65)",
          color: "#0a0806",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.32), 0 10px 28px -8px rgba(232,207,154,0.45)",
        }}
        data-testid={`first-plate-next-${step}`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{nextLabel} <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}
